import {FC, Fragment, useState} from 'react'
import {Option} from 'react-multi-select-component'
import {KTIcon} from '../../../../../../_metronic/helpers'
import {formatDate} from '../../../../../../utils/dayJs/format'
import {useMetric} from '../../core/MetricProvider'
import {useMetricOptions} from '../../core/useMetricOptions'
import {MetricClient} from '../../core/_models'
import {formatMoney, formatNumber} from '../../core/_format'
import {ListPagination} from '../pagination/ListPagination'

const COLUMNS = [
  'Nome',
  'Dt Cadastro',
  'Dt Nascimento',
  'Sexo',
  'E-mail',
  'Telefones',
  'CPF',
  'Como conheceu',
  'Status',
  'Distrato',
  'Qtd vendas',
  'Valor total',
  'Ticket médio',
  'Faixa de compra',
  'Dias desde a última compra',
  'Faixa da última compra',
  'Total de sessões',
]

const labelOf = (options: Option[], value: string | null) =>
  options.find((option) => option.value === value)?.label ?? value ?? '-'

// Sessões, realizadas e valor por código de produto do cliente.
const ClientProducts: FC<{client: MetricClient; products: Option[]}> = ({client, products}) => {
  const rows = products.filter((product) => client.sessions[product.value] || client.amounts[product.value])

  if (!rows.length) {
    return <div className='text-muted py-3'>Nenhuma compra no período.</div>
  }

  return (
    <table className='table table-sm align-middle fs-7 gy-2 mb-0 w-auto'>
      <thead>
        <tr className='text-muted fw-bolder text-uppercase'>
          <th className='min-w-50px'>Código</th>
          <th className='min-w-150px'>Produto</th>
          <th className='text-end min-w-100px'>Sessões</th>
          <th className='text-end min-w-100px'>Realizadas</th>
          <th className='text-end min-w-100px'>Valor</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((product) => (
          <tr key={product.value}>
            <td>{product.value}</td>
            <td>{product.label}</td>
            <td className='text-end'>{formatNumber(client.sessions[product.value])}</td>
            <td className='text-end'>{formatNumber(client.sessionsRealized[product.value])}</td>
            <td className='text-end'>{formatMoney(client.amounts[product.value])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const ClientsTab = () => {
  const {clients} = useMetric()
  const {products, genders} = useMetricOptions()
  const [expanded, setExpanded] = useState<number[]>([])

  const toggle = (id: number) =>
    setExpanded((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    )

  const data = clients?.data ?? []

  return (
    <>
      <div className='table-responsive mb-10'>
        <table className='table align-middle table-row-dashed fs-6 gy-5'>
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
              <th className='w-25px'></th>
              {COLUMNS.map((column) => (
                <th key={column} className='min-w-100px'>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='text-gray-600 fw-bold'>
            {data.length > 0 ? (
              data.map((client) => (
                <Fragment key={client.id}>
                  <tr>
                    <td>
                      <button
                        type='button'
                        className='btn btn-sm btn-icon btn-light'
                        title='Detalhe por produto'
                        onClick={() => toggle(client.id)}
                      >
                        <KTIcon
                          iconName={expanded.includes(client.id) ? 'minus' : 'plus'}
                          className='fs-3'
                        />
                      </button>
                    </td>
                    <td className='min-w-200px'>{client.name}</td>
                    <td>{formatDate(client.createdAt)}</td>
                    <td>{client.birthDate ?? '-'}</td>
                    <td>{client.gender ? labelOf(genders, client.gender) : '-'}</td>
                    <td>{client.email ?? '-'}</td>
                    <td className='min-w-150px'>{client.phones ?? '-'}</td>
                    <td>{client.document ?? '-'}</td>
                    <td>{client.howMet}</td>
                    <td>{client.status}</td>
                    <td>{client.hasDistract ? 'Sim' : 'Não'}</td>
                    <td>{formatNumber(client.salesCount)}</td>
                    <td>{formatMoney(client.totalAmount)}</td>
                    <td>{formatMoney(client.averageTicket)}</td>
                    <td>{client.purchaseRangeLabel}</td>
                    <td>{client.daysSinceLastPurchase ?? '-'}</td>
                    <td>{client.lastPurchaseRangeLabel}</td>
                    <td>{formatNumber(client.totalSessions)}</td>
                  </tr>
                  {expanded.includes(client.id) && (
                    <tr>
                      <td></td>
                      <td colSpan={COLUMNS.length} className='bg-light rounded'>
                        <ClientProducts client={client} products={products} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={COLUMNS.length + 1}>
                  <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                    Nenhum registro encontrado
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <ListPagination />
    </>
  )
}

export {ClientsTab}
